// Admin v2 — AI keresés és szűrés (Figma ADM_16, 16b, 16c).
// Globális keresés a fő kollekciókban (helyszínek/klubok, felhasználók, versenyek, ötletek) relevancia-pontozással;
// keresési statisztika és szűrődefiníciók: app_settings/search.
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, doc, getDocs, increment, limit, onSnapshot, query, setDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import { Icon } from '../icons';
import { fmtInt } from '../data';
import { Badge, Button, Card, Empty, Field, IconTile, Input, Modal, PageHeader, SectionTitle, Segmented, Select, Spinner, StatCard, Tabs, Toggle, Tone, useToast } from '../ui';

type Kind = 'club' | 'user' | 'tournament' | 'idea';
interface Hit { kind: Kind; id: string; title: string; sub: string; score: number; city: string; active: boolean }
interface Filter { id: string; name: string; type: 'multi' | 'range' | 'bool' | 'date'; entity: Kind; options: string[]; order: number; enabled: boolean }

const REF = () => doc(db, 'app_settings', 'search');
const KIND: Record<Kind, { t: string; icon: string; tone: Tone; plural: string }> = {
  club: { t: 'Helyszín', icon: 'Buildings', tone: 'cyan', plural: 'Helyszínek' },
  user: { t: 'Felhasználó', icon: 'UserCircle', tone: 'purple', plural: 'Felhasználók' },
  tournament: { t: 'Verseny', icon: 'Trophy', tone: 'gold', plural: 'Versenyek' },
  idea: { t: 'Ötlet', icon: 'Lightning', tone: 'green', plural: 'Ötletek' },
};
const norm = (s: string) => s.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');
const keyOf = (q: string) => norm(q).replace(/[.#$/[\]]/g, ' ').trim().slice(0, 80);

/** Egyszerű relevancia: teljes egyezés > szó eleji egyezés > részegyezés, plusz token-lefedettség. */
function score(text: string, q: string): number {
  const t = norm(text);
  const n = norm(q.trim());
  if (!n) return 0;
  if (t === n) return 100;
  let s = 0;
  if (t.startsWith(n)) s += 60; else if (t.includes(` ${n}`)) s += 45; else if (t.includes(n)) s += 30;
  const tokens = n.split(/\s+/).filter(Boolean);
  const hit = tokens.filter((x) => t.includes(x)).length;
  if (tokens.length > 1) s += (hit / tokens.length) * 30;
  return s;
}

export default function SearchPage() {
  const toast = useToast();
  const nav = useNavigate();
  const [tab, setTab] = useState<'search' | 'analytics' | 'filters'>('search');
  const [data, setData] = useState<Hit[] | null>(null);
  const [q, setQ] = useState('');
  const [submitted, setSubmitted] = useState('');
  const [kinds, setKinds] = useState<Kind[]>([]);
  const [city, setCity] = useState('');
  const [onlyActive, setOnlyActive] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [cfg, setCfg] = useState<{ queries: Record<string, number>; zero: Record<string, number>; filters: Filter[]; total: number }>({ queries: {}, zero: {}, filters: [], total: 0 });
  const [edit, setEdit] = useState<Filter | 'new' | null>(null);

  useEffect(() => onSnapshot(REF(), (s) => { const v = s.data() ?? {}; setCfg({ queries: v.queries ?? {}, zero: v.zero ?? {}, filters: v.filters ?? [], total: Number(v.total ?? 0) }); }, () => undefined), []);
  useEffect(() => {
    Promise.all([
      getDocs(collection(db, 'clubs')).then((s) => s.docs.map((d) => { const v = d.data(); return { kind: 'club' as Kind, id: d.id, title: String(v.name ?? ''), sub: [v.city, v.address, v.boardCount ? `${v.boardCount} tábla` : ''].filter(Boolean).join(' · '), city: String(v.city ?? ''), active: v.verified === true, score: 0 }; })).catch(() => []),
      getDocs(query(collection(db, 'users'), limit(3000))).then((s) => s.docs.map((d) => { const v = d.data(); return { kind: 'user' as Kind, id: d.id, title: String(v.displayName || v.username || ''), sub: [v.username && `@${v.username}`, v.email, v.onboarding?.city].filter(Boolean).join(' · '), city: String(v.onboarding?.city ?? ''), active: true, score: 0 }; })).catch(() => []),
      getDocs(collection(db, 'tournaments')).then((s) => s.docs.map((d) => { const v = d.data(); return { kind: 'tournament' as Kind, id: d.id, title: String(v.name ?? ''), sub: [v.venueName, v.gameMode, `${(v.participantIds ?? []).length} nevező`].filter(Boolean).join(' · '), city: '', active: v.status === 'upcoming' || v.status === 'ongoing', score: 0 }; })).catch(() => []),
      getDocs(collection(db, 'user_ideas')).then((s) => s.docs.map((d) => { const v = d.data(); return { kind: 'idea' as Kind, id: d.id, title: String(v.title ?? ''), sub: `Ötlet · ${v.upvotes ?? 0} szavazat`, city: String(v.region ?? ''), active: v.status !== 'rejected', score: 0 }; })).catch(() => []),
    ]).then((all) => setData(all.flat()));
  }, []);

  const results = useMemo(() => {
    if (!data || !submitted.trim()) return [];
    return data.map((h) => ({ ...h, score: Math.max(score(h.title, submitted), score(h.sub, submitted) * 0.6) }))
      .filter((h) => h.score > 0 && (!kinds.length || kinds.includes(h.kind)) && (!city || norm(h.city).includes(norm(city))) && (!onlyActive || h.active))
      .sort((a, b) => b.score - a.score).slice(0, 50);
  }, [data, submitted, kinds, city, onlyActive]);

  const run = async () => {
    const term = q.trim();
    setSubmitted(term);
    if (!term || !data) return;
    const k = keyOf(term);
    const n = data.filter((h) => Math.max(score(h.title, term), score(h.sub, term)) > 0).length;
    setDoc(REF(), { total: increment(1), queries: { [k]: increment(1) }, ...(n === 0 ? { zero: { [k]: increment(1) } } : {}) }, { merge: true }).catch(() => undefined);
  };
  const open = (h: Hit) => nav(h.kind === 'user' ? `/users/${h.id}` : h.kind === 'club' ? '/clubs' : h.kind === 'tournament' ? '/tournament' : '/ideabox');

  const top = Object.entries(cfg.queries).sort((a, b) => b[1] - a[1]).slice(0, 5);
  const zero = Object.entries(cfg.zero).sort((a, b) => b[1] - a[1]).slice(0, 5);
  const zeroTotal = Object.values(cfg.zero).reduce((a, b) => a + b, 0);

  return (
    <div>
      <PageHeader title="AI keresés és szűrés" sub="Keresési algoritmusok, szűrők és analitika konfigurálása" right={<Button icon="Plus" onClick={() => setEdit('new')}>Szűrő hozzáadása</Button>} />
      <Tabs value={tab} onChange={setTab} options={[{ value: 'search', label: 'Keresés' }, { value: 'analytics', label: 'Analitika' }, { value: 'filters', label: 'Szűrők kezelése' }]} />

      {tab === 'search' && (
        <div>
          <form className="flex gap-3" onSubmit={(e) => { e.preventDefault(); run(); }}>
            <Input icon="MagnifyingGlass" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Keresés helyszínek, felhasználók, versenyek és ötletek között..." className="flex-1" />
            <Button kind="ghost" icon="SlidersHorizontal" onClick={() => setShowFilters((x) => !x)}>Szűrők</Button>
          </form>
          {showFilters && (
            <Card className="!p-4 mt-3 flex items-center gap-4 flex-wrap">
              <div className="flex gap-1.5 flex-wrap">{(Object.keys(KIND) as Kind[]).map((k) => (
                <button key={k} type="button" onClick={() => setKinds((x) => (x.includes(k) ? x.filter((y) => y !== k) : [...x, k]))}
                  className={`h-[30px] px-3 rounded-full text-[12px] font-semibold ${kinds.includes(k) ? 'bg-[#00AFF2] text-white' : 'bg-[#1d232a] text-[#B8B8C3]'}`}>{KIND[k].plural}</button>
              ))}</div>
              <Input value={city} onChange={(e) => setCity(e.target.value)} placeholder="Város" className="w-[180px]" />
              <span className="inline-flex items-center gap-2 text-[13px] text-white"><Toggle checked={onlyActive} onChange={setOnlyActive} />Csak aktív</span>
            </Card>
          )}
          {(kinds.length > 0 || city || onlyActive) && (
            <div className="flex items-center gap-2 mt-3 flex-wrap text-[12px]">
              <span className="text-[#8a8f99]">Szűrők:</span>
              {kinds.map((k) => <Badge key={k} tone="cyan">{KIND[k].plural}</Badge>)}
              {city && <Badge tone="cyan">{city}</Badge>}
              {onlyActive && <Badge tone="green">Aktív</Badge>}
              <button type="button" onClick={() => { setKinds([]); setCity(''); setOnlyActive(false); }} className="font-semibold text-[#00AFF2] hover:underline">Összes törlése</button>
            </div>
          )}
          {data === null ? <Spinner /> : submitted ? (
            <>
              <div className="mt-4 mb-3 text-[12px] text-[#8a8f99]">{results.length} találat</div>
              {results.length === 0 ? <Card><Empty>Nincs találat erre: „{submitted}”.</Empty></Card> : (
                <div className="flex flex-col gap-2.5">
                  {results.map((h) => (
                    <button key={`${h.kind}${h.id}`} type="button" onClick={() => open(h)} className="flex items-center gap-3 p-4 rounded-2xl bg-[#15191E] border border-[#31383D] text-left hover:border-[#00AFF2]/50">
                      <IconTile icon={KIND[h.kind].icon} tone={KIND[h.kind].tone} />
                      <span className="flex-1 min-w-0"><span className="block text-[14px] font-semibold text-white truncate">{h.title}</span><span className="block text-[12px] text-[#8a8f99] truncate">{KIND[h.kind].t} · {h.sub}</span></span>
                      <Icon name="CaretRight" size={16} className="text-[#8a8f99]" />
                    </button>
                  ))}
                </div>
              )}
            </>
          ) : <div className="mt-6 text-[13px] text-[#8a8f99]">Írj be egy kifejezést, majd nyomj Entert.</div>}
        </div>
      )}

      {tab === 'analytics' && (
        <div>
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
            <StatCard icon="MagnifyingGlass" tone="cyan" label="Összes keresés" value={fmtInt(cfg.total)} />
            <StatCard icon="Sparkle" tone="purple" label="Egyedi lekérdezések" value={fmtInt(Object.keys(cfg.queries).length)} valueColor="#a855f7" />
            <StatCard icon="ChartBar" tone="green" label="Találati arány" value={cfg.total ? `${Math.round(((cfg.total - zeroTotal) / cfg.total) * 100)}%` : '—'} valueColor="#2bc48a" />
            <StatCard icon="Warning" tone="gold" label="Nulla találat" value={fmtInt(zeroTotal)} valueColor="#d9a514" />
          </div>
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 mt-6">
            <Card className="!p-5"><SectionTitle>Top keresések</SectionTitle>
              {top.length === 0 ? <Empty>Még nincs keresés.</Empty> : top.map(([k, n], i) => (
                <div key={k} className={`flex items-center gap-3 h-[40px] text-[13px] ${i < top.length - 1 ? 'border-b border-[#31383D]' : ''}`}><span className="w-4 text-[#8a8f99]">{i + 1}</span><span className="flex-1 text-white truncate">{k}</span><span className="text-[12px] text-[#8a8f99]">{fmtInt(n)} keresés</span></div>
              ))}
            </Card>
            <Card className="!p-5"><SectionTitle>Nulla találatos keresések</SectionTitle>
              {zero.length === 0 ? <Empty>Nincs ilyen keresés.</Empty> : zero.map(([k, n], i) => (
                <div key={k} className={`flex items-center gap-3 h-[40px] text-[13px] ${i < zero.length - 1 ? 'border-b border-[#31383D]' : ''}`}><Icon name="Warning" size={15} className="text-[#d9a514]" /><span className="flex-1 text-white truncate">{k}</span><span className="text-[12px] text-[#8a8f99]">{n}×</span></div>
              ))}
            </Card>
          </div>
        </div>
      )}

      {tab === 'filters' && (cfg.filters.length === 0 ? <Card><Empty>Még nincs szűrő.</Empty></Card> : (
        <div className="flex flex-col gap-2.5">
          {[...cfg.filters].sort((a, b) => a.order - b.order).map((f) => (
            <Card key={f.id} className="!p-4 flex items-center gap-3">
              <span className="w-6 text-center text-[13px] text-[#8a8f99]">{f.order}</span>
              <div className="flex-1 min-w-0"><div className="text-[14px] font-semibold text-white">{f.name}</div><div className="text-[12px] text-[#8a8f99] truncate">{KIND[f.entity].plural} · {f.options.join(', ') || '—'}</div></div>
              <Badge tone={f.enabled ? 'green' : 'gray'}>{f.enabled ? 'Engedélyezve' : 'Letiltva'}</Badge>
              <button type="button" onClick={() => setEdit(f)} aria-label="Szerkesztés" className="inline-flex items-center justify-center w-[32px] h-[32px] rounded-[10px] bg-[#1d232a] border border-[#31383D] text-[#B8B8C3]"><Icon name="PencilSimpleLine" size={15} /></button>
              <button type="button" onClick={async () => { await setDoc(REF(), { filters: cfg.filters.filter((x) => x.id !== f.id) }, { merge: true }); toast('Szűrő törölve.'); }} aria-label="Törlés" className="inline-flex items-center justify-center w-[32px] h-[32px] rounded-[10px] bg-[#1d232a] border border-[#31383D] text-[#ef4444]"><Icon name="Trash" size={15} /></button>
            </Card>
          ))}
        </div>
      ))}

      {edit && <FilterModal f={edit === 'new' ? null : edit} next={cfg.filters.length + 1} onClose={() => setEdit(null)} onSave={async (f) => {
        await setDoc(REF(), { filters: cfg.filters.some((x) => x.id === f.id) ? cfg.filters.map((x) => (x.id === f.id ? f : x)) : [...cfg.filters, f] }, { merge: true });
        toast('Szűrő mentve.'); setTab('filters');
      }} />}
    </div>
  );
}

function FilterModal({ f, next, onClose, onSave }: { f: Filter | null; next: number; onClose: () => void; onSave: (f: Filter) => Promise<void> }) {
  const [v, setV] = useState<Filter>(f ?? { id: `flt_${Date.now().toString(36)}`, name: '', type: 'multi', entity: 'club', options: [], order: next, enabled: true });
  const [opts, setOpts] = useState(v.options.join(', '));
  return (
    <Modal title={f ? 'Szűrő szerkesztése' : 'Új szűrő'} onClose={onClose} width={500}
      footer={<><Button kind="ghost" onClick={onClose}>Mégse</Button><Button disabled={!v.name.trim()} onClick={async () => { await onSave({ ...v, name: v.name.trim(), options: opts.split(',').map((x) => x.trim()).filter(Boolean) }); onClose(); }}>Mentés</Button></>}>
      <div className="flex flex-col gap-4">
        <Field label="Szűrő neve"><Input value={v.name} onChange={(e) => setV({ ...v, name: e.target.value })} placeholder="pl. Kategória szűrő" /></Field>
        <div><div className="text-[13px] font-medium text-[#B8B8C3] mb-2">Típus</div>
          <Segmented size="sm" value={v.type} onChange={(t) => setV({ ...v, type: t })} options={[{ value: 'multi', label: 'Többszörös választás' }, { value: 'range', label: 'Tartomány' }, { value: 'bool', label: 'Logikai' }, { value: 'date', label: 'Dátum' }]} /></div>
        <Field label="Entitás típus"><Select value={v.entity} onChange={(e) => setV({ ...v, entity: e as Kind })} options={(Object.keys(KIND) as Kind[]).map((k) => ({ value: k, label: KIND[k].plural }))} /></Field>
        <div><Field label="Opciók"><Input value={opts} onChange={(e) => setOpts(e.target.value)} placeholder="steel tip, soft tip, mindkettő" /></Field><div className="mt-1.5 text-[11px] text-[#8a8f99]">Vesszővel elválasztott szűrő opciók listája</div></div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Sorrend"><Input inputMode="numeric" value={String(v.order)} onChange={(e) => setV({ ...v, order: Number(e.target.value.replace(/[^\d]/g, '')) || 1 })} /></Field>
          <div className="flex items-end"><div className="flex items-center justify-between w-full px-4 h-[44px] rounded-xl bg-[#1d232a]"><span className="text-[13px] font-semibold text-white">Szűrő engedélyezése</span><Toggle checked={v.enabled} onChange={(x) => setV({ ...v, enabled: x })} /></div></div>
        </div>
      </div>
    </Modal>
  );
}
